(() => {
  const root = document.documentElement;
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let header = document.querySelector('[data-rosemary-header]');
  let overlay = document.querySelector('[data-header-overlay]');
  const toast = document.querySelector('[data-toast]');
  let search = document.querySelector('[data-search-overlay]');
  let mobileMenu = document.querySelector('[data-mobile-menu]');
  let lastScrollY = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
  let scrollDirection = 0;
  let directionDistance = 0;
  let scrollFrame = null;
  let activeMegaItem = null;
  let megaOpenTimer = null;
  let megaCloseTimer = null;
  let searchTimer = null;
  let searchRequest = null;
  let activeSurface = null;
  let activeOpener = null;
  let overlayHideTimer = null;
  const OPEN_DELAY = 100;
  const CLOSE_DELAY = 220;
  const OVERLAY_DURATION = prefersReduced ? 0 : 800;
  const desktopQuery = window.matchMedia('(min-width: 990px)');

  const getHeader = () => {
    if (header?.isConnected) return header;
    header = document.querySelector('[data-rosemary-header]');
    return header;
  };

  const showStickyHeader = () => {
    const currentHeader = getHeader();
    currentHeader?.classList.remove('is-hidden');
    currentHeader?.classList.add('is-visible');
  };

  const hideStickyHeader = () => {
    const currentHeader = getHeader();
    currentHeader?.classList.remove('is-visible');
    currentHeader?.classList.add('is-hidden');
  };

  const getScrollY = () => window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

  const refreshElements = () => {
    header = document.querySelector('[data-rosemary-header]');
    overlay = document.querySelector('[data-header-overlay]');
    search = document.querySelector('[data-search-overlay]');
    mobileMenu = document.querySelector('[data-mobile-menu]');
  };

  const getFocusable = (container) => [...(container?.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])') || [])]
    .filter((element) => !element.closest('[aria-hidden="true"]') && element.getClientRects().length);

  const setBackgroundInert = (inert) => {
    const main = document.getElementById('MainContent');
    const footer = document.querySelector('.rosemary-footer');
    [main, footer].forEach((element) => {
      if (!element) return;
      if (inert) element.setAttribute('inert', '');
      else element.removeAttribute('inert');
    });
  };

  const setPanelOpen = (element, open) => {
    if (!element) return;
    if (open) element.removeAttribute('hidden');
    element.classList.toggle('is-open', open);
    element.setAttribute('aria-hidden', open ? 'false' : 'true');
  };

  const setOverlay = (open) => {
    if (!overlay) return;
    window.clearTimeout(overlayHideTimer);
    if (open) {
      overlay.hidden = false;
      window.requestAnimationFrame(() => overlay?.classList.add('is-open'));
      return;
    }
    overlay.classList.remove('is-open');
    overlayHideTimer = window.setTimeout(() => {
      if (overlay && !overlay.classList.contains('is-open')) overlay.hidden = true;
    }, OVERLAY_DURATION);
  };

  const anyPanelOpen = () => Boolean(activeSurface || activeMegaItem);

  const closeCart = () => {
    const drawer = document.querySelector('[data-cart-drawer][aria-hidden="false"]');
    if (typeof drawer?.close === 'function') drawer.close({ restoreFocus: false });
  };

  const syncOverlay = () => {
    const megaOverlay = Boolean(activeMegaItem) && getHeader()?.dataset.megaOverlay !== 'false';
    setOverlay(megaOverlay);
  };

  const closeSurface = ({ restoreFocus = true } = {}) => {
    if (!activeSurface) return;
    const closing = activeSurface;
    setPanelOpen(closing, false);
    activeSurface = null;
    document.querySelectorAll('[data-search-open],[data-mobile-menu-open]').forEach(button => button.setAttribute('aria-expanded', 'false'));
    window.RosemaryScrollLock?.unlock('header');
    setBackgroundInert(false);
    if (restoreFocus && activeOpener?.isConnected && activeOpener.matches(':focus-visible,button,a,input')) {
      window.setTimeout(() => activeOpener?.focus({ preventScroll: true }), prefersReduced ? 0 : 260);
    }
    activeOpener = null;
  };

  const openSurface = (panel, opener) => {
    if (!panel || activeSurface === panel) return;
    closeCart();
    closeMegaMenu({ immediateOverlay: true });
    closeSurface({ restoreFocus: false });
    activeSurface = panel;
    activeOpener = opener || document.activeElement;
    showStickyHeader();
    setPanelOpen(panel, true);
    opener?.setAttribute?.('aria-expanded', 'true');
    window.RosemaryScrollLock?.lock('header');
    setBackgroundInert(true);
    const delay = prefersReduced ? 0 : 90;
    window.setTimeout(() => {
      const focusable = getFocusable(panel);
      const preferred = panel.querySelector('input[type="search"]');
      (preferred || focusable[0])?.focus({ preventScroll: true });
    }, delay);
  };

  const openMegaMenu = (item) => {
    if (!item || !desktopQuery.matches || activeSurface) return;
    window.clearTimeout(megaCloseTimer);
    window.clearTimeout(megaOpenTimer);
    megaOpenTimer = window.setTimeout(() => {
      const previous = activeMegaItem;
      if (previous === item) return;
      activeMegaItem = item;
      const nextMenu = item.querySelector('[data-mega-menu]');
      const nextTrigger = item.querySelector('[data-mega-trigger]');
      nextMenu?.classList.add('is-open');
      nextMenu?.setAttribute('aria-hidden', 'false');
      nextTrigger?.setAttribute('aria-expanded', 'true');
      showStickyHeader();
      if (previous) {
        getHeader()?.classList.add('is-mega-switching');
        window.requestAnimationFrame(() => {
          previous.querySelector('[data-mega-menu]')?.classList.remove('is-open');
          previous.querySelector('[data-mega-menu]')?.setAttribute('aria-hidden', 'true');
          previous.querySelector('[data-mega-trigger]')?.setAttribute('aria-expanded', 'false');
          window.setTimeout(() => getHeader()?.classList.remove('is-mega-switching'), prefersReduced ? 0 : 180);
        });
      }
      syncOverlay();
    }, activeMegaItem ? 0 : OPEN_DELAY);
  };

  function closeMegaMenu({ immediateOverlay = false } = {}) {
    window.clearTimeout(megaOpenTimer);
    if (!activeMegaItem) return;
    activeMegaItem.querySelector('[data-mega-menu]')?.classList.remove('is-open');
    activeMegaItem.querySelector('[data-mega-menu]')?.setAttribute('aria-hidden', 'true');
    activeMegaItem.querySelector('[data-mega-trigger]')?.setAttribute('aria-expanded', 'false');
    activeMegaItem = null;
    if (immediateOverlay) setOverlay(false);
    else syncOverlay();
  }

  const scheduleMegaClose = () => {
    window.clearTimeout(megaCloseTimer);
    megaCloseTimer = window.setTimeout(closeMegaMenu, CLOSE_DELAY);
  };

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('[data-search-open]')) {
      event.preventDefault();
      openSurface(search, target.closest('[data-search-open]'));
    }
    if (target.closest('[data-search-close]')) closeSurface();
    if (target.closest('[data-mobile-menu-open]')) {
      event.preventDefault();
      openSurface(mobileMenu, target.closest('[data-mobile-menu-open]'));
    }
    if (target.closest('[data-mobile-menu-close]')) closeSurface();
    if (target === overlay) closeMegaMenu();
    if (target === search || target === mobileMenu) closeSurface();
    if (target.closest('a[href]') && (activeSurface || activeMegaItem)) {
      closeSurface({ restoreFocus: false });
      closeMegaMenu();
    }
    if (!target.closest('[data-mega-item]') && !target.closest('[data-header-overlay]') && !target.closest('[data-search-overlay]') && !target.closest('[data-mobile-menu]')) closeMegaMenu();
  });

  document.addEventListener('mouseover', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest('[data-mega-trigger]');
    const item = trigger?.closest('[data-mega-item]');
    if (item?.querySelector('[data-mega-menu]')) openMegaMenu(item);
  });

  document.addEventListener('mouseout', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const item = target.closest('[data-mega-item]');
    if (item && !item.contains(event.relatedTarget)) scheduleMegaClose();
  });

  document.addEventListener('focusin', (event) => {
    const searchTrigger = event.target.closest?.('[data-search-open]');
    if (searchTrigger && !searchTrigger.closest('[data-search-overlay]')) openSurface(search, searchTrigger);
    const item = event.target.closest?.('[data-mega-item]');
    if (item?.querySelector('[data-mega-menu]')) openMegaMenu(item);
    else if (activeMegaItem && !activeMegaItem.contains(event.target)) scheduleMegaClose();
  });

  document.addEventListener('keydown', (event) => {
    const megaTrigger = event.target.closest?.('[data-mega-trigger]');
    if (megaTrigger && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      openMegaMenu(megaTrigger.closest('[data-mega-item]'));
      return;
    }
    if (event.key === 'Escape') {
      if (activeSurface) closeSurface();
      else closeMegaMenu();
      return;
    }
    if (event.key !== 'Tab' || !activeSurface) return;
    const focusable = getFocusable(activeSurface);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('toggle', (event) => {
    const details = event.target.closest?.('[data-mobile-accordion]');
    if (!details) return;
    details.querySelector('summary')?.setAttribute('aria-expanded', String(details.open));
  }, true);

  document.addEventListener('click', (event) => {
    const summary = event.target.closest?.('[data-mobile-accordion] > summary');
    if (!summary) return;
    event.preventDefault();
    const details = summary.parentElement;
    const submenu = details.querySelector('[data-mobile-submenu]');
    if (!submenu) return;
    const open = !details.open;
    details.closest('[data-mobile-menu]')?.querySelectorAll('[data-mobile-accordion][open]').forEach(other => {
      if (other === details) return;
      const otherMenu = other.querySelector('[data-mobile-submenu]');
      otherMenu.style.height = `${otherMenu.scrollHeight}px`;
      window.requestAnimationFrame(() => { otherMenu.style.height = '0px'; otherMenu.style.opacity = '0'; });
      other.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      window.setTimeout(() => { other.open = false; }, prefersReduced ? 0 : 500);
    });
    if (open) {
      details.open = true;
      summary.setAttribute('aria-expanded', 'true');
      submenu.style.height = '0px';
      submenu.style.opacity = '0';
      window.requestAnimationFrame(() => {
        submenu.style.height = `${submenu.scrollHeight}px`;
        submenu.style.opacity = '1';
      });
      submenu.addEventListener('transitionend', () => {
        if (details.open) submenu.style.height = 'auto';
      }, { once: true });
    } else {
      submenu.style.height = `${submenu.scrollHeight}px`;
      window.requestAnimationFrame(() => { submenu.style.height = '0px'; submenu.style.opacity = '0'; });
      summary.setAttribute('aria-expanded', 'false');
      window.setTimeout(() => { details.open = false; }, prefersReduced ? 0 : 500);
    }
  });

  const escapeHtml = (value) => String(value || '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  })[character]);

  const renderPredictiveResults = (container, results) => {
    const resources = results?.resources?.results || {};
    const items = [
      ...(resources.products || []),
      ...(resources.collections || []),
      ...(resources.pages || []),
      ...(resources.articles || [])
    ];

    if (!items.length) {
      container.innerHTML = `<p>${escapeHtml(search?.dataset.emptyText || '')}</p>`;
      container.classList.remove('is-loading');
      window.requestAnimationFrame(() => container.classList.add('has-results'));
      return;
    }

    container.innerHTML = `
      <div class="rh-search__results-grid">
        ${items.slice(0, 12).map((item) => `
          <a class="rh-search__result" href="${escapeHtml(item.url)}">
            ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy">` : ''}
            <span>${escapeHtml(item.title)}</span>
          </a>
        `).join('')}
      </div>
    `;
    container.classList.remove('is-loading');
    window.requestAnimationFrame(() => container.classList.add('has-results'));
  };

  document.addEventListener('input', (event) => {
    const input = event.target.closest?.('[data-predictive-input]');
    if (!input || !search) return;
    const container = search.querySelector('[data-predictive-results]');
    if (!container) return;
    const query = input.value.trim();

    window.clearTimeout(searchTimer);
    searchRequest?.abort();
    if (query.length < 2) {
      container.innerHTML = '';
      container.classList.remove('is-loading', 'has-results');
      return;
    }

    container.classList.remove('has-results');
    container.classList.add('is-loading');
    container.innerHTML = `<p>${escapeHtml(search.dataset.loadingText || '')}</p>`;
    searchTimer = window.setTimeout(async () => {
      try {
        searchRequest = new AbortController();
        const base = search.dataset.predictiveUrl || '/search/suggest';
        const url = `${base}.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article&resources[limit]=4`;
        const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: searchRequest.signal });
        if (!response.ok) throw new Error('Predictive search failed');
        renderPredictiveResults(container, await response.json());
      } catch (error) {
        if (error.name === 'AbortError') return;
        container.innerHTML = `<p>${escapeHtml(search.dataset.emptyText || '')}</p>`;
        container.classList.remove('is-loading');
        window.requestAnimationFrame(() => container.classList.add('has-results'));
      }
    }, 200);
  });

  const onScroll = () => {
    const currentHeader = getHeader();
    if (currentHeader?.dataset.stickyEnabled !== 'false') {
      const currentScrollY = Math.max(getScrollY(), 0);
      const difference = currentScrollY - lastScrollY;
      const topThreshold = 80;
      const directionThreshold = 5;
      const cartIsOpen = Boolean(document.querySelector('[data-cart-drawer][aria-hidden="false"]'));
      const isLockedOpen = anyPanelOpen() || cartIsOpen;

      if (difference !== 0) {
        const nextDirection = difference > 0 ? 1 : -1;
        if (nextDirection !== scrollDirection) {
          scrollDirection = nextDirection;
          directionDistance = Math.abs(difference);
        } else {
          directionDistance += Math.abs(difference);
        }
      }

      if (isLockedOpen || currentHeader.dataset.hideOnScroll === 'false') {
        showStickyHeader();
        directionDistance = 0;
      } else if (currentScrollY <= topThreshold) {
        showStickyHeader();
        currentHeader.classList.remove('is-scrolled');
        directionDistance = 0;
      } else {
        currentHeader.classList.add('is-scrolled');
        if (scrollDirection > 0 && directionDistance >= directionThreshold) {
          hideStickyHeader();
          directionDistance = 0;
        } else if (scrollDirection < 0 && directionDistance >= directionThreshold) {
          showStickyHeader();
          directionDistance = 0;
        }
      }
      lastScrollY = currentScrollY;
    }
    if (!prefersReduced) {
      document.querySelectorAll('[data-parallax]').forEach((el) => {
        const rect = el.getBoundingClientRect();
        const offset = Math.max(-22, Math.min(22, (window.innerHeight / 2 - rect.top) * 0.025));
        el.style.setProperty('--parallax-y', `${offset}px`);
      });
    }
  };
  onScroll();
  const requestScrollSync = () => {
    if (scrollFrame) return;
    scrollFrame = window.requestAnimationFrame(() => {
      scrollFrame = null;
      onScroll();
    });
  };
  window.addEventListener('scroll', requestScrollSync, { passive: true });
  document.addEventListener('shopify:section:load', () => {
    closeSurface({ restoreFocus: false });
    closeMegaMenu();
    refreshElements();
    lastScrollY = getScrollY();
    onScroll();
  });
  document.addEventListener('shopify:section:unload', () => {
    closeSurface({ restoreFocus: false });
    closeMegaMenu();
  });
  document.addEventListener('shopify:block:select', event => {
    const item = event.target.closest?.('[data-mega-item]');
    if (item) openMegaMenu(item);
  });
  document.addEventListener('shopify:block:deselect', closeMegaMenu);
  document.addEventListener('cart-drawer:open', () => {
    showStickyHeader();
    closeSurface({ restoreFocus: false });
    closeMegaMenu({ immediateOverlay: true });
    lastScrollY = Math.max(getScrollY(), 0);
  });
  document.addEventListener('cart-drawer:close', () => {
    showStickyHeader();
    lastScrollY = Math.max(getScrollY(), 0);
  });
  desktopQuery.addEventListener?.('change', () => {
    closeSurface({ restoreFocus: false });
    closeMegaMenu({ immediateOverlay: true });
  });
  window.addEventListener('pagehide', () => {
    closeSurface({ restoreFocus: false });
    closeMegaMenu({ immediateOverlay: true });
    window.RosemaryScrollLock?.unlock('header');
  });

  if ('IntersectionObserver' in window && !prefersReduced) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.16 });
    document.querySelectorAll('.r-reveal').forEach((el) => observer.observe(el));
  } else {
    document.querySelectorAll('.r-reveal').forEach((el) => el.classList.add('is-visible'));
  }

  document.querySelectorAll('[data-carousel]').forEach((carousel) => {
    const track = carousel.querySelector('[data-carousel-track]');
    if (!track) return;
    const dots = carousel.querySelector('[data-carousel-dots]');
    const previousButton = carousel.querySelector('[data-carousel-prev]');
    const nextButton = carousel.querySelector('[data-carousel-next]');
    const updateCarouselButtons = () => {
      const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
      const position = root.dir === 'rtl' ? Math.abs(track.scrollLeft) : track.scrollLeft;
      if (previousButton) previousButton.disabled = position <= 2;
      if (nextButton) nextButton.disabled = position >= maximum - 2;
    };
    const scrollByCard = (direction) => {
      const firstCard = track.firstElementChild;
      const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 0;
      const amount = firstCard ? firstCard.getBoundingClientRect().width + gap : 280;
      track.scrollBy({ left: amount * direction, behavior: prefersReduced ? 'auto' : 'smooth' });
    };
    previousButton?.addEventListener('click', () => scrollByCard(root.dir === 'rtl' ? 1 : -1));
    nextButton?.addEventListener('click', () => scrollByCard(root.dir === 'rtl' ? -1 : 1));
    track.addEventListener('scroll', updateCarouselButtons, { passive: true });
    updateCarouselButtons();
    if (dots) {
      const items = [...track.children];
      items.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
        dot.addEventListener('click', () => {
          const item = items[index];
          if (item) track.scrollTo({ left: item.offsetLeft, behavior: prefersReduced ? 'auto' : 'smooth' });
        });
        dots.append(dot);
      });
    }
    const section = carousel.closest('[data-autoplay]');
    const autoplayEnabled = section?.dataset.autoplay === 'true';
    const autoplaySpeed = Number(section?.dataset.autoplaySpeed || 5) * 1000;
    if (autoplayEnabled && !prefersReduced) {
      window.setInterval(() => scrollByCard(root.dir === 'rtl' ? -1 : 1), autoplaySpeed);
    }
  });

  document.querySelectorAll('[data-category-slider]').forEach((track) => {
    const slider = track.closest('.r-category-slider');
    const previousButton = slider?.querySelector('[data-category-prev]');
    const nextButton = slider?.querySelector('[data-category-next]');
    let dragging = false;
    let moved = false;
    let startX = 0;
    let startScroll = 0;

    const categoryScrollPosition = () => root.dir === 'rtl' ? Math.abs(track.scrollLeft) : track.scrollLeft;
    const updateCategoryArrows = () => {
      const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
      const position = categoryScrollPosition();
      if (previousButton) previousButton.disabled = position <= 2;
      if (nextButton) nextButton.disabled = position >= maximum - 2;
    };
    const scrollCategory = (direction) => {
      const card = track.querySelector('.r-category');
      const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 0;
      const amount = card ? card.getBoundingClientRect().width + gap : track.clientWidth * .5;
      track.scrollBy({ left: amount * direction, behavior: prefersReduced ? 'auto' : 'smooth' });
    };

    previousButton?.addEventListener('click', () => scrollCategory(root.dir === 'rtl' ? 1 : -1));
    nextButton?.addEventListener('click', () => scrollCategory(root.dir === 'rtl' ? -1 : 1));
    track.addEventListener('scroll', updateCategoryArrows, { passive: true });
    updateCategoryArrows();

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      dragging = true;
      moved = false;
      startX = event.clientX;
      startScroll = track.scrollLeft;
      track.classList.add('is-dragging');
      track.setPointerCapture(event.pointerId);
    });

    track.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const distance = event.clientX - startX;
      if (Math.abs(distance) > 4) moved = true;
      track.scrollLeft = startScroll - distance;
    });

    const stopCategoryDrag = (event) => {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
      if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
    };

    track.addEventListener('pointerup', stopCategoryDrag);
    track.addEventListener('pointercancel', stopCategoryDrag);
    track.addEventListener('click', (event) => {
      if (!moved) return;
      event.preventDefault();
      event.stopPropagation();
      moved = false;
    }, true);
  });

  document.querySelectorAll('[data-hero-slider]').forEach((slider) => {
    const track = slider.querySelector('[data-hero-track]');
    if (!track) return;
    const slides = [...track.children];
    const dots = slider.querySelector('[data-hero-dots]');
    const autoplayEnabled = slider.dataset.autoplay === 'true';
    const autoplaySpeed = Math.max(Number(slider.dataset.autoplaySpeed || 5) * 1000, 2000);
    let activeIndex = 0;
    let autoplayTimer = null;

    const stopAutoplay = () => {
      if (autoplayTimer) window.clearTimeout(autoplayTimer);
      autoplayTimer = null;
    };

    const restartAutoplay = () => {
      stopAutoplay();
      const dotButtons = [...(dots?.querySelectorAll('button') || [])];
      dotButtons.forEach((dot) => dot.classList.remove('is-progressing'));
      if (!autoplayEnabled || slides.length <= 1 || prefersReduced) return;

      const activeDot = dotButtons[activeIndex];
      if (activeDot) {
        activeDot.style.setProperty('--hero-progress-duration', `${autoplaySpeed}ms`);
        void activeDot.offsetWidth;
        activeDot.classList.add('is-progressing');
      }

      autoplayTimer = window.setTimeout(() => goTo(activeIndex + 1), autoplaySpeed);
    };

    const goTo = (index) => {
      if (!slides.length) return;
      activeIndex = (index + slides.length) % slides.length;
      const slide = slides[activeIndex];
      track.scrollTo({ left: slide.offsetLeft, behavior: prefersReduced ? 'auto' : 'smooth' });
      dots?.querySelectorAll('button').forEach((dot, dotIndex) => {
        dot.setAttribute('aria-current', String(dotIndex === activeIndex));
      });
      restartAutoplay();
    };
    dots?.replaceChildren();
    slides.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.setAttribute('aria-label', `Go to banner ${index + 1}`);
      dot.setAttribute('aria-current', String(index === 0));
      dot.addEventListener('click', () => goTo(index));
      dots?.append(dot);
    });
    slider.querySelector('[data-hero-prev]')?.addEventListener('click', () => goTo(activeIndex - 1));
    slider.querySelector('[data-hero-next]')?.addEventListener('click', () => goTo(activeIndex + 1));
    restartAutoplay();
  });

  document.querySelectorAll('[data-tabs]').forEach((tabsRoot) => {
    const tabs = [...tabsRoot.querySelectorAll('[role="tab"]')];
    const panels = [...tabsRoot.querySelectorAll('[data-panel]')];
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const id = tab.getAttribute('data-tab');
        tabs.forEach((item) => item.setAttribute('aria-selected', String(item === tab)));
        panels.forEach((panel) => {
          panel.hidden = panel.getAttribute('data-panel') !== id;
        });
      });
    });
  });

  let wishlistCount = Number(localStorage.getItem('rosemaryWishlistCount') || 0);
  const wishlistCountNode = document.querySelector('[data-wishlist-count]');
  const cartCountNode = document.querySelector('[data-cart-count]');
  if (cartCountNode) {
    let previousCartCount = cartCountNode.textContent.trim();
    new MutationObserver(() => {
      const nextCartCount = cartCountNode.textContent.trim();
      if (nextCartCount === previousCartCount) return;
      previousCartCount = nextCartCount;
      cartCountNode.classList.remove('is-updating');
      void cartCountNode.offsetWidth;
      cartCountNode.classList.add('is-updating');
      cartCountNode.addEventListener('animationend', () => cartCountNode.classList.remove('is-updating'), { once: true });
    }).observe(cartCountNode, { childList: true, characterData: true, subtree: true });
  }
  const updateWishlist = () => {
    if (wishlistCountNode) wishlistCountNode.textContent = String(wishlistCount);
    localStorage.setItem('rosemaryWishlistCount', String(wishlistCount));
  };
  updateWishlist();

  const showToast = (message) => {
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(showToast.timeout);
    showToast.timeout = window.setTimeout(() => toast.classList.remove('is-visible'), 2400);
  };
  const cartToastText = toast?.dataset.cartToast || '';
  const wishlistToastText = toast?.dataset.wishlistToast || '';

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const wishlist = target.closest('[data-wishlist-add]');
    if (wishlist) {
      wishlistCount += 1;
      updateWishlist();
      showToast(wishlistToastText);
    }

    const mockAdd = target.closest('[data-mock-add]');
    if (mockAdd) {
      const current = Number(cartCountNode?.textContent || 0) + 1;
      if (cartCountNode) cartCountNode.textContent = String(current);
      showToast(cartToastText);
    }
  });

  document.addEventListener('cart-drawer:open', async () => {
    const lastCountSync = Number(window.RosemaryCart?.lastCountSync || 0);
    if (Date.now() - lastCountSync < 2000) return;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((res) => res.json());
      if (cartCountNode) cartCountNode.textContent = String(cart.item_count);
    } catch (error) {
      return;
    }
  });
})();
